import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { Check, AlertCircle } from "lucide-react";
import { Reveal, Eyebrow } from "@/lib/motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SIZES = ["50–100", "101–250", "251–500"];

function Field({ label, error, children, htmlFor }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-ink-muted"
      >
        {label}
      </label>
      {children}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-1.5 flex items-center gap-1.5 text-xs text-warning"
          >
            <AlertCircle size={12} />
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: { name: "", email: "", company: "", size: "", role: "" },
    mode: "onTouched",
  });

  const onSubmit = () => {
    // Client-side only demo — no backend. Show confirmation.
    setSubmitted(true);
  };

  const inputCls =
    "w-full rounded-lg border border-hairline bg-bg-primary/60 px-4 py-3 font-sans text-sm text-ink placeholder:text-ink-muted/60 transition-colors focus:border-signal/60 focus:outline-none";

  return (
    <section id="contact" className="relative bg-bg-primary/55 py-24 backdrop-blur-sm lg:py-32">
      <div className="container-max band-pad grid grid-cols-1 gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div>
          <Reveal>
            <Eyebrow testId="contact-eyebrow">Contact · demo request</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-3xl font-semibold leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Get a workflow audit.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md leading-relaxed text-ink-muted">
              Tell us where the handoffs stall. We’ll map your current ops flow and
              show you where RelayOps routes around the drag.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 font-mono text-xs text-ink-muted">
              RelayOps, Inc. · Chicago, IL
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-hairline bg-bg-surface p-7 sm:p-9">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  data-testid="contact-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex min-h-[360px] flex-col items-center justify-center text-center"
                >
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-signal/15 text-signal">
                    <Check size={30} strokeWidth={2.5} />
                  </span>
                  <p className="mt-6 max-w-sm font-display text-xl font-medium text-ink">
                    Thanks — someone from RelayOps will follow up within one
                    business day.
                  </p>
                  <span className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-ink-muted">
                    request logged · status: queued
                  </span>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="contact-form"
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="flex flex-col gap-5"
                >
                  <Field label="Name" error={errors.name?.message} htmlFor="name">
                    <input
                      id="name"
                      data-testid="input-name"
                      className={inputCls}
                      placeholder="Jordan Rivera"
                      {...register("name", { required: "Name is required" })}
                    />
                  </Field>

                  <Field
                    label="Work email"
                    error={errors.email?.message}
                    htmlFor="email"
                  >
                    <input
                      id="email"
                      data-testid="input-email"
                      type="email"
                      className={inputCls}
                      placeholder="jordan@company.com"
                      {...register("email", {
                        required: "Work email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Enter a valid email address",
                        },
                      })}
                    />
                  </Field>

                  <Field
                    label="Company"
                    error={errors.company?.message}
                    htmlFor="company"
                  >
                    <input
                      id="company"
                      data-testid="input-company"
                      className={inputCls}
                      placeholder="Acme Logistics"
                      {...register("company", { required: "Company is required" })}
                    />
                  </Field>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Company size" error={errors.size?.message}>
                      <Controller
                        control={control}
                        name="size"
                        rules={{ required: "Select a size" }}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger
                              data-testid="select-size"
                              className={`${inputCls} h-auto justify-between`}
                            >
                              <SelectValue placeholder="Select range" />
                            </SelectTrigger>
                            <SelectContent className="border-hairline bg-bg-surface text-ink">
                              {SIZES.map((s) => (
                                <SelectItem
                                  key={s}
                                  value={s}
                                  data-testid={`size-${s}`}
                                >
                                  {s} employees
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </Field>

                    <Field label="Role" error={errors.role?.message} htmlFor="role">
                      <input
                        id="role"
                        data-testid="input-role"
                        className={inputCls}
                        placeholder="Ops Manager"
                        {...register("role", { required: "Role is required" })}
                      />
                    </Field>
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    className="mt-2 rounded-full bg-signal px-6 py-3.5 font-sans text-sm font-semibold text-bg-primary transition-transform duration-200 hover:scale-[1.02] active:scale-95"
                  >
                    Request my workflow audit
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
